import React, { useState } from "react";
import { connect } from "react-redux";
import { arrayOf, bool, func, object, shape, string } from "prop-types";
import { Link } from "react-router-dom";
import { markDocumentReadDavinFoods } from "../../redux/actions";
import { getReadingListWithStatus } from "../../common/OnboardingSectionCreator/helpers";
import ReadingListDownload from "../ReadingListDownload/index";
import OnboardingListItem from "../OnboardingListItem/OnboardingListItem";
import history from "../../history";
import Results from "./Results";

const DavinFoodsOnboarding = ({
  markDocumentRead,
  readingLoading,
  readingList,
  onboarding,
  quizLoading,
  quizShowResults,
}) => {
  const Status = Object.freeze({
    Todo: 1,
    Pending: 2,
    Done: 3,
  });
  const [reading, setReading] = useState(null);
  const readDocuments = onboarding.readDocuments || [];
  const readingListWithStatus = getReadingListWithStatus(
    readingList,
    readDocuments
  );

  const getQuizStatus = (quizName) => {
    const { status, retries } = onboarding[quizName] || {};
    if (status === "Pass" || retries === 0) {
      return Status.Done;
    }
    if (status === "Fail" && retries > 0) {
      return Status.Pending;
    }
    return Status.Todo;
  };

  const isQuizResults = (quizName) => !!onboarding[quizName];
  const getQuizResults = (quizName) => onboarding[quizName];
  const isQuizLoading = (quizName) => quizLoading === quizName;

  const getCheckListStatus = (typeCode, formName) => {
    const isFormStarted = !!onboarding[formName];
    const isFormCompleted = onboarding[typeCode];

    if (isFormStarted && !isFormCompleted) {
      return Status.Pending;
    }
    if (isFormCompleted) {
      return Status.Done;
    }
    return Status.Todo;
  };

  const redirectBack = (url) => history.push(url);

  const quizListWithStatus = [
    {
      status: getQuizStatus("chemicalSafetyQuiz"),
      name: "chemicalSafetyQuiz",
      title: "Chemical Safety and Safe Cleaning Quiz",
      text: "Complete",
      url: "davin-foods-onboarding/chemical-safety-quiz",
      loading: isQuizLoading("chemicalSafetyQuiz"),
      downloadingDisabled: true,
    },
    {
      status: getQuizStatus("evacuationQuiz"),
      name: "evacuationQuiz",
      title: "Fire Controls and Evacuation Quiz",
      text: "Complete",
      url: "davin-foods-onboarding/evacuation-quiz",
      loading: isQuizLoading("evacuationQuiz"),
      downloadingDisabled: true,
    },
    {
      status: getQuizStatus("foodSafetyQuiz"),
      name: "foodSafetyQuiz",
      title: "Food Safety Quiz",
      text: "Complete",
      url: "davin-foods-onboarding/food-safety-quiz",
      loading: isQuizLoading("foodSafetyQuiz"),
      downloadingDisabled: true,
    },
    {
      status: getQuizStatus("healthAndSafetyQuiz"),
      name: "healthAndSafetyQuiz",
      title: "Health & Safety Quiz",
      text: "Complete",
      url: "davin-foods-onboarding/health-and-safety-quiz",
      loading: isQuizLoading("healthAndSafetyQuiz"),
      downloadingDisabled: true,
    },
    {
      status: getQuizStatus("foodAllergensQuiz"),
      name: "foodAllergensQuiz",
      title: "Managing Food Allergens Quiz",
      text: "Complete",
      url: "davin-foods-onboarding/food-allergens-quiz",
      loading: isQuizLoading("foodAllergensQuiz"),
      downloadingDisabled: true,
    },
    {
      status: getQuizStatus("manualHandlingQuiz"),
      name: "manualHandlingQuiz",
      title: "Manual Handling Quiz",
      text: "Complete",
      url: "davin-foods-onboarding/manual-handling-quiz",
      loading: isQuizLoading("manualHandlingQuiz"),
      downloadingDisabled: true,
    },
  ];

  const checkListWithStatus = [
    {
      status: getCheckListStatus("YDF7", "inductionChecklist"),
      title: "Induction Checklist",
      text: "Complete",
      url: "davin-foods-onboarding/induction-checklist",
      loading: false,
      downloadingDisabled: true,
    },
  ];

  return (
    <div className="container text-bluishGray pb-2">
      <header className="row">
        <div className="col-12">
          <h2 className="section-header pt-2">Davin Foods Induction Pack</h2>
        </div>
      </header>
      <div className="row">
        <div className="col-lg-6">
          <div className="section-body pt-1 mb-2 bg-white">
            <h5 className="px-1">Reading List</h5>
            <p className="p-1" style={{ width: "90%" }}>
              You are required to download and read each document below before
              you start work. You will then be asked to tick the box to confirm
              you have read and understood and agree to each document. If you
              have any queries, please speak to your HRGO Consultant.
            </p>
            {readingListWithStatus &&
              readingListWithStatus.map((elem, i) => (
                <ReadingListDownload
                  key={i}
                  title={elem.title}
                  link={elem.link}
                  status={elem.status}
                  agreementText={`I have read and understood the ${elem.title}.`}
                  currentlyReading={reading}
                  setCurrentlyReading={setReading}
                  markDocumentRead={markDocumentRead}
                  isLoading={readingLoading}
                />
              ))}
          </div>
        </div>
        <div className="col-lg-6">
          <div className="section-body pt-1 mb-2 bg-white">
            <h5 className="px-1">Induction Checklist</h5>
            <p className="p-1">
              Please complete the checklist below. If you have any queries,
              please speak to your HRGO Consultant.
            </p>
            {checkListWithStatus.map((elem, i) => (
              <OnboardingListItem
                key={i}
                title={elem.title}
                status={elem.status}
                btnText={elem.text}
                loading={elem.loading}
                downloadingDisabled={elem.downloadingDisabled}
                handleClick={() => redirectBack(elem.url)}
              />
            ))}
          </div>
          <div className="section-body pt-1 mb-2 bg-white">
            <h5 className="px-1">Quiz</h5>
            <p className="p-1">
              Please complete each quiz below. The pass mark is 100% and you
              have 3 retries. If you have any queries, please speak to your HR
              GO Consultant.
            </p>
            {quizListWithStatus.map((elem, i) => (
              <>
                {isQuizResults(elem.name) && !isQuizLoading(elem.name) && (
                  <Results
                    quizResults={getQuizResults(elem.name)}
                    title={elem.title}
                    name={elem.name}
                    quizShowResults={quizShowResults}
                  />
                )}
                <OnboardingListItem
                  key={i}
                  title={elem.title}
                  status={elem.status}
                  btnText={elem.text}
                  loading={elem.loading}
                  downloadingDisabled={elem.downloadingDisabled}
                  handleClick={() => redirectBack(elem.url)}
                />
              </>
            ))}
          </div>
        </div>
        <div className="w-100 d-flex justify-content-end mt-2 mr-2">
          <Link to="/profile" className="btn btn-primary px-3">
            Back to My Account
          </Link>
        </div>
      </div>
    </div>
  );
};

DavinFoodsOnboarding.propTypes = {
  markDocumentRead: func.isRequired,
  onboarding: object,
  readingLoading: bool,
  quizLoading: string,
  quizShowResults: string,
  readingList: arrayOf(
    shape({
      title: string.isRequired,
      link: string.isRequired,
    })
  ),
};
const mapStateToProps = (state) => ({
  onboarding: state.core.app.davinFoodsOnboarding,
  readingLoading: state.core.davinFoods.isReadingLoading,
  readingList: state.core.umbracoContent.davinFoodsReadingList,
  quizLoading: state.core.davinFoods.quizLoading,
  quizShowResults: state.core.davinFoods.quizShowResults,
});
const mapDispatchToProps = {
  markDocumentRead: markDocumentReadDavinFoods,
};
const withRedux = connect(mapStateToProps, mapDispatchToProps);
export default withRedux(DavinFoodsOnboarding);
